// =============================================================================
// PlansSection - Piani e abbonamento (anteprima)
// =============================================================================
// Vetrina dei piani previsti: serve a valutare la UX del listino, la gestione
// dell'abbonamento non è ancora attiva (nessuna CTA porta a un checkout).
// I prezzi sono in euro/mese; la fatturazione annuale mostra il prezzo mensile
// equivalente pagando 12 mesi in anticipo.
// =============================================================================

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CreditCard, Sparkles } from 'lucide-react';
import type { TenantDetail } from '../../api/hooks';
import { Badge, SectionHeader, SettingsCard } from './_shared';

type PlanId = 'free' | 'pro' | 'max' | 'enterprise';
type BillingCycle = 'monthly' | 'yearly';

interface Plan {
  id: PlanId;
  /// Prezzo mensile pagando mese per mese
  monthly: number;
  /// Prezzo mensile equivalente con fatturazione annuale
  yearly: number;
  highlighted?: boolean;
}

const PLANS: Plan[] = [
  { id: 'free', monthly: 0, yearly: 0 },
  { id: 'pro', monthly: 29, yearly: 25, highlighted: true },
  { id: 'max', monthly: 49, yearly: 40 },
  { id: 'enterprise', monthly: 130, yearly: 100 },
];

/// I tier salvati sul tenant non coincidono ancora con gli id del listino
const TIER_TO_PLAN: Record<string, PlanId> = {
  free: 'free',
  starter: 'pro',
  pro: 'pro',
  professional: 'max',
  max: 'max',
  enterprise: 'enterprise',
};

interface PlansSectionProps {
  tenant: TenantDetail;
}

export function PlansSection({ tenant }: PlansSectionProps) {
  const { t } = useTranslation('settings');
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  const currentPlan = TIER_TO_PLAN[tenant.subscriptionTier] ?? 'free';
  const isTrialing = !!tenant.trialEndsAt && new Date(tenant.trialEndsAt) > new Date();

  return (
    <SettingsCard>
      <SectionHeader
        icon={CreditCard}
        title={t('organization.plan')}
        description={t('plans.description')}
        action={<Badge tone="amber">{t('organization.comingSoon')}</Badge>}
      />

      {/* Ciclo di fatturazione */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex p-1 bg-gray-100 rounded-lg">
          {(['monthly', 'yearly'] as BillingCycle[]).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                cycle === c
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t(`plans.cycle.${c}`)}
            </button>
          ))}
        </div>
        {cycle === 'yearly' && (
          <span className="text-sm text-emerald-700 font-medium">{t('plans.yearlySaving')}</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {PLANS.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            cycle={cycle}
            isCurrent={plan.id === currentPlan}
          />
        ))}
      </div>

      {isTrialing && (
        <div className="mt-5 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg inline-block">
          {t('organization.trialUntil', {
            date: new Date(tenant.trialEndsAt!).toLocaleDateString(),
          })}
        </div>
      )}

      <p className="mt-5 text-sm text-gray-500">{t('organization.comingSoonMessage')}</p>
    </SettingsCard>
  );
}

// -----------------------------------------------------------------------------
// Card di un singolo piano
// -----------------------------------------------------------------------------

interface PlanCardProps {
  plan: Plan;
  cycle: BillingCycle;
  isCurrent: boolean;
}

function PlanCard({ plan, cycle, isCurrent }: PlanCardProps) {
  const { t } = useTranslation('settings');
  const price = cycle === 'yearly' ? plan.yearly : plan.monthly;
  const features = t(`plans.${plan.id}.features`, { returnObjects: true }) as string[];

  return (
    <div
      className={`relative flex flex-col rounded-xl border p-5 transition-colors ${
        isCurrent
          ? 'border-emerald-500 bg-emerald-50/40 ring-1 ring-emerald-500/30'
          : plan.highlighted
            ? 'border-gray-300 bg-white shadow-sm'
            : 'border-gray-200 bg-white'
      }`}
    >
      {/* Etichetta in alto: piano attuale o suggerito */}
      {(isCurrent || plan.highlighted) && (
        <span className="absolute -top-2.5 left-5">
          {isCurrent ? (
            <Badge tone="emerald">{t('organization.currentPlan')}</Badge>
          ) : (
            <Badge tone="purple" className="gap-1">
              <Sparkles className="w-3 h-3" />
              {t('plans.popular')}
            </Badge>
          )}
        </span>
      )}

      <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mt-1">
        {t(`plans.${plan.id}.name`)}
      </h4>

      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-gray-900">{price}</span>
        <span className="text-sm text-gray-500">€{t('plans.perMonth')}</span>
      </div>
      <p className="mt-1 text-xs text-gray-500 min-h-[2rem]">
        {price === 0
          ? t('plans.freeForever')
          : cycle === 'yearly'
            ? t('plans.billedYearly', { total: price * 12 })
            : t('plans.billedMonthly')}
      </p>

      <p className="mt-2 text-sm text-gray-600">{t(`plans.${plan.id}.tagline`)}</p>

      <ul className="mt-4 space-y-2 flex-1">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Nessuna azione reale: la gestione abbonamento non è ancora attiva */}
      <button
        type="button"
        disabled
        className={`mt-5 w-full h-10 rounded-lg text-sm font-medium cursor-not-allowed ${
          isCurrent
            ? 'bg-white text-emerald-700 border border-emerald-300'
            : 'bg-gray-100 text-gray-400'
        }`}
      >
        {isCurrent ? t('plans.currentCta') : t('plans.chooseCta')}
      </button>
    </div>
  );
}
